<template>
  <div class="chat-interface">
    <!-- History Sidebar (left) using reusable component -->
    <ConversationHistory
      :conversations="conversations"
      :activeConversationId="currentConversationId"
      @create-chat="createNewConversation"
      @select-conversation="switchConversation"
      @delete-conversation="handleDeleteConversation"
    />

    <!-- Main Chat Area (center) -->
    <div class="chat-main">
      <!-- Messages Area -->
      <div class="messages-container">
        <div class="messages-list">
          <ChatMessage
            v-for="message in messages"
            :key="message.id || `msg-${messages.indexOf(message)}`"
            :message="message"
          />

          <!-- Loading indicator -->
          <div v-if="isLoading" class="loading-indicator">
            <div class="spinner"></div>
            <span>Assistant is thinking...</span>
          </div>
        </div>
      </div>

      <!-- Chat Input (always at bottom) -->
      <ChatInput
        ref="chatInput"
        :disabled="isLoading"
        @send="sendMessage"
      />
    </div>

    <!-- Context Panel (shown after first message) -->
    <ProjectContextPanel
      v-if="messages.length > 0"
      :conversation="contextData"
      :problemId="currentProblemId"
      class="context-sidebar"
    />
  </div>
</template>

<script>
import ChatMessage from "./ChatMessage.vue";
import ChatInput from "./ChatInput.vue";
import ProjectContextPanel from "./ProjectContextPanel.vue";
import ConversationHistory from "./ConversationHistory.vue";
import { apiClient } from "@/plugins/apiService.js";

export default {
  name: "ChatInterface",
  components: {
    ChatMessage,
    ChatInput,
    ProjectContextPanel,
    ConversationHistory,
  },
  props: {
    problemId: {
      type: Number,
      default: null,
    },
    conversationId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      messages: [],
      isLoading: false,
      currentConversationId: null,
      currentProblemId: null,
      conversations: [],
      contextData: {
        problem_statement: "",
        living_lab: "",
        buildings: [],
        stakeholders: [],
        kpis: [],
        goals: [],
      },
    };
  },
  async mounted() {
    // Load conversation if provided via route
    if (this.conversationId) {
      this.currentConversationId = this.conversationId;
      await this.loadConversation(this.conversationId);
      
      // Check if there's an initial message in the query params
      // Only send if conversation is empty (fresh conversation, not a reload)
      const initialMessage = this.$route.query.initialMessage;
      if (initialMessage && this.messages.length === 0) {
        this.$nextTick(() => {
          this.sendMessage(decodeURIComponent(initialMessage));
        });
        
        // Remove initialMessage from query params to prevent resending on reload
        this.$router.replace({
          name: "ChatPage",
          params: { id: this.conversationId },
        });
      }
    }
    
    // Load conversation history
    await this.loadConversations();
  },
  watch: {
    conversationId(newId) {
      // Reload conversation when route params change
      if (newId) {
        this.currentConversationId = newId;
        this.loadConversation(newId);
      }
    },
  },
  methods: {
    async loadConversations() {
      try {
        const response = await apiClient.get("/chat?limit=50");
        this.conversations = response.data.conversations || [];
      } catch (error) {
        console.error("Error loading conversations:", error);
      }
    },

    createNewConversation() {
      // Navigate back to home to start a new conversation
      this.$router.push("/");
    },

    async switchConversation(conversationId) {
      // Navigate to the conversation page
      this.$router.push({
        name: "ChatPage",
        params: { id: conversationId },
      });
    },

    handleDeleteConversation(conversationId) {
      // Remove conversation from list
      this.conversations = this.conversations.filter(
        (conv) => conv.id !== conversationId
      );

      // If deleted conversation was active, navigate to home
      if (this.currentConversationId === conversationId) {
        this.$router.push("/");
      }
    },

    async createConversation() {
      try {
        const response = await apiClient.post("/chat");
        this.currentConversationId = response.data.conversation_id;
        this.currentProblemId = response.data.problem_id;
        this.messages = [];
        this.showWelcome = false;

        // Add to history
        this.conversations.unshift({
          id: this.currentConversationId,
          problem_id: this.currentProblemId,
          created_at: new Date().toISOString(),
          stage: "problem_understanding",
        });

        // Auto-focus input
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      } catch (error) {
        console.error("Error creating conversation:", error);
        alert("Failed to start conversation. Please try again.");
      }
    },

    async loadConversation(conversationId) {
      try {
        const response = await apiClient.get(`/chat/${conversationId}`);
        this.currentConversationId = conversationId;
        this.currentProblemId = response.data.conversation.problem_id;
        
        // Normalize messages to ensure tools are accessible
        this.messages = (response.data.messages || []).map(msg => {
          // Extract tools from metadata if available
          if (msg.metadata && msg.metadata.tools && !msg.tools) {
            msg.tools = msg.metadata.tools;
          }
          return msg;
        });

        console.log(response);
        

        // Initialize context data from conversation
        this.contextData = {
          problem_statement: response.data.conversation.problem_statement || "",
          living_lab: "",
          buildings: [],
          stakeholders: [],
          kpis: [],
          goals: [],
        };

        // Restore additional context data if available
        if (this.currentProblemId) {
          await this.loadProblemContext();
        }
      } catch (error) {
        console.error("Error loading conversation:", error);
      }
    },

    async loadProblemContext() {
      if (!this.currentProblemId) return;
      try {
        const response = await apiClient.get(
          `/problem/${this.currentProblemId}`,
        );
        // Parse problem data and populate context
        const problem = response.data;
        this.contextData = {
          problem_statement: problem.need || this.contextData.problem_statement || "",
          living_lab: "TBD", // Would load from location
          buildings: [], // Would load from problem_building junction
          stakeholders: [], // Would load from problem_stakeholder junction
          kpis: [], // Would load from problem_kpi junction
          goals: [], // Would load from goals
        };
      } catch (error) {
        console.error("Error loading problem context:", error);
      }
    },

    async sendMessage(messageText) {
      // Add user message to UI immediately
      const userMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: messageText,
        message_type: "text",
        created_at: new Date().toISOString(),
      };
      this.messages.push(userMessage);

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // Send to backend
      this.isLoading = true;
      try {
        const response = await apiClient.post(
          `/chat/${this.currentConversationId}/message`,
          { message: messageText },
        );

        // Add assistant message
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: response.data.message,
          message_type: response.data.message_type || "text",
          metadata: response.data.metadata,
          tools: response.data.tools,
          created_at: new Date().toISOString(),
        };
        this.messages.push(assistantMessage);

        // Update problem ID if created
        if (response.data.problem_id) {
          this.currentProblemId = response.data.problem_id;
        }

        // Reload conversation to get any updates from LLM tools (e.g., problem statement)
        if (this.currentConversationId) {
          const convResponse = await apiClient.get(`/chat/${this.currentConversationId}`);
          this.contextData.problem_statement = convResponse.data.conversation.problem_statement || this.contextData.problem_statement;
        }

        // Scroll to bottom
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error("Error sending message:", error);

        // Add error message
        const errorMessage = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          message_type: "error",
          created_at: new Date().toISOString(),
        };
        this.messages.push(errorMessage);
      } finally {
        this.isLoading = false;

        // Refocus input
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    },

    scrollToBottom() {
      const container = this.$el.querySelector(".messages-list");
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 0);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.chat-interface {
  display: flex;
  width: 100%;
  height: 100%;
  gap: 1px;
  position: relative;
  background-color: #f5f5f5;
}

/* Center: Main Chat */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: white;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--secondaryColorLight);
    border-radius: 4px;

    &:hover {
      background: var(--secondaryColor);
    }
  }
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--darkColor);
  font-size: 0.9rem;
  padding: 12px;

  .spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid var(--secondaryColorLight);
    border-radius: 50%;
    border-top-color: var(--primaryColor);
    animation: spin 0.8s linear infinite;
  }
}

/* Right Sidebar: Context */
.context-sidebar {
  flex: 0 0 300px;
  border-left: 1px solid #e5e5e5;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive: Tablet */
@media (max-width: 1200px) {
  .context-sidebar {
    flex: 0 0 250px;
  }
}

/* Responsive: Small screens */
@media (max-width: 1024px) {
  .chat-interface {
    flex-direction: column;
  }

  .context-sidebar {
    flex: 0 0 200px;
    border-left: none;
    border-top: 1px solid #e5e5e5;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .context-sidebar {
    display: none;
  }

  .chat-interface {
    gap: 0;
  }

  .chat-main {
    width: 100%;
  }

  .messages-container {
    padding: 12px;
  }
}
</style>
